import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
export const Route = createFileRoute("/user/_layout/crm/opportunities")({
  component: OpportunitiesPage,
});

import { useState, useEffect, useMemo } from "react";
import instance from "@/lib/axios";
import { getCrmUrl } from "@/lib/crm";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { OpportunityTable } from "@/components/crm/tables/OpportunityTable";
import { OpportunityKanbanBoard } from "@/components/crm/tables/OpportunityKanbanBoard";
import {
  OpportunityFormSheet,
  OPPORTUNITY_STAGES,
} from "@/components/crm/forms/OpportunityFormSheet";
import {
  useFetchOpportunities,
  useOpportunityMutations,
  type OpportunityOrdering,
} from "@/hooks/api/crm/useOpportunities";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { AssigneeFilter } from "@/components/crm/AssigneeFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";
import type {
  CrmOpportunity,
  CrmOpportunityPayload,
  OpportunityStage,
} from "@/types/crm";

const KANBAN_PAGE_SIZE = 200;

function OpportunitiesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const viewId = new URLSearchParams(location.searchStr).get("view") ?? undefined;

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebouncedSearch] = useDebounce(searchQuery, 500);
  const [stageFilter, setStageFilter] = useState("");
  // Undefined = all team-visible records; pick a user to narrow.
  const [assignedTo, setAssignedTo] = useState<number | undefined>(
    undefined
  );
  const [showFilters, setShowFilters] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        stage: SortDirection;
        amount: SortDirection;
        probability: SortDirection;
        close_date: SortDirection;
        assigned_to__first_name: SortDirection;
      },
      OpportunityOrdering
    >({
      name: null,
      stage: null,
      amount: null,
      probability: null,
      close_date: null,
      assigned_to__first_name: null,
    });

  const [viewMode, setViewMode] = useState<"table" | "kanban">("table");

  const opportunitiesQuery = useFetchOpportunities(
    viewMode === "kanban"
      ? {
          search: debouncedSearch || undefined,
          stage: stageFilter || undefined,
          assigned_to: assignedTo ? String(assignedTo) : undefined,
          page: 1,
          page_size: KANBAN_PAGE_SIZE,
        }
      : {
          page,
          page_size: pageSize,
          search: debouncedSearch || undefined,
          stage: stageFilter || undefined,
          assigned_to: assignedTo ? String(assignedTo) : undefined,
          ordering: getApiOrdering(),
        }
  );
  const opportunities = opportunitiesQuery.data?.results ?? [];
  const count = opportunitiesQuery.data?.count ?? 0;

  const { createOpportunity, updateOpportunity, deleteOpportunity } =
    useOpportunityMutations();

  const formMutating = createOpportunity.isPending || updateOpportunity.isPending;
  const isMutating = formMutating || deleteOpportunity.isPending;
  const isLoading = opportunitiesQuery.isPending || isMutating;
  const isFetching = opportunitiesQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((count ?? 0) / pageSize)),
    [count, pageSize]
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingOpp, setEditingOpp] = useState<CrmOpportunity | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [oppToDelete, setOppToDelete] = useState<CrmOpportunity | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (!viewId) return;
    instance
      .get<CrmOpportunity>(getCrmUrl(`opportunities/${viewId}/`))
      .then((res) => {
        setEditingOpp(res.data);
        setIsViewOnly(true);
        setIsSheetOpen(true);
      })
      .catch(() =>
        navigate({
          to: "/user/crm/opportunities",
          search: {},
        })
      );
  }, [viewId]);

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    setIsViewOnly(false);
    if (viewId)
      navigate({ to: "/user/crm/opportunities", search: {} });
  };

  const resetFilters = () => {
    setSearchQuery("");
    resetDebouncedSearch();
    setStageFilter("");
    setAssignedTo(undefined);
    setOrdering({
      name: null,
      stage: null,
      amount: null,
      probability: null,
      close_date: null,
      assigned_to__first_name: null,
    });
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, stageFilter, assignedTo, ordering, viewMode, setPage]);

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
    }
  };

  const handleStageChange = async (id: string, newStage: OpportunityStage) => {
    try {
      await updateOpportunity.mutateAsync({ id, payload: { stage: newStage } });
      toast.success("Stage updated.");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const onCreate = async (data: CrmOpportunityPayload) => {
    await createOpportunity.mutateAsync(data);
    toast.success("Opportunity created successfully!");
    setIsSheetOpen(false);
  };

  const onEdit = async (id: string, data: Partial<CrmOpportunityPayload>) => {
    await updateOpportunity.mutateAsync({ id, payload: data });
    toast.success("Opportunity updated successfully!");
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!oppToDelete) return;
    try {
      await deleteOpportunity.mutateAsync({ id: oppToDelete.id });
      toast.success("Opportunity deleted successfully!");
      setIsDeleteDialogOpen(false);
      setOppToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Opportunities"
          description="Manage your sales pipeline."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={() => {
            setEditingOpp(null);
            setIsSheetOpen(true);
          }}
          addLabel="Add Opportunity"
          isLoading={isFetching}
        >
          <div className="flex items-center rounded-md border overflow-hidden">
            <Button
              variant={viewMode === "table" ? "default" : "ghost"}
              size="sm"
              className="rounded-none h-8 px-2.5"
              onClick={() => setViewMode("table")}
              title="Table view"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "kanban" ? "default" : "ghost"}
              size="sm"
              className="rounded-none h-8 px-2.5"
              onClick={() => setViewMode("kanban")}
              title="Kanban view"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </PageHeader>

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search opportunities..."
          />
          <Select
            value={stageFilter}
            onValueChange={(v) => setStageFilter(v === "all" ? "" : v)}
            disabled={isFetching}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {OPPORTUNITY_STAGES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <AssigneeFilter value={assignedTo} onChange={setAssignedTo} />
        </FiltersPanel>

        {viewMode === "kanban" && count !== null && count > KANBAN_PAGE_SIZE && (
          <div className="rounded-md border border-yellow-300 bg-yellow-50 px-4 py-2 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300">
            Showing the first {KANBAN_PAGE_SIZE} of {count} opportunities. Use
            filters or table view to see all records.
          </div>
        )}

        {viewMode === "table" ? (
          <>
            <OpportunityTable
              opportunities={opportunities}
              onView={(o) =>
                navigate({
                  to: "/user/crm/opportunities/$id",
                  params: { id: o.id },
                })
              }
              onViewAccount={(accountId) =>
                navigate({
                  to: "/user/crm/accounts/$id",
                  params: { id: accountId },
                })
              }
              onEdit={(o) => {
                setEditingOpp(o);
                setIsSheetOpen(true);
              }}
              onDelete={(o) => {
                setOppToDelete(o);
                setIsDeleteDialogOpen(true);
              }}
              isLoading={isLoading}
              ordering={ordering}
              onOrderingChange={toggleOrdering}
            />
            <PaginationComponent
              count={count}
              page={page}
              onPageChange={handlePageChange}
              isLoading={isFetching}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        ) : (
          <OpportunityKanbanBoard
            opportunities={opportunities}
            onView={(o) =>
              navigate({
                to: "/user/crm/opportunities/$id",
                params: { id: o.id },
              })
            }
            onEdit={(o) => {
              setEditingOpp(o);
              setIsSheetOpen(true);
            }}
            onDelete={(o) => {
              setOppToDelete(o);
              setIsDeleteDialogOpen(true);
            }}
            onStageChange={handleStageChange}
            isLoading={isLoading}
          />
        )}

        <OpportunityFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={handleCloseSheet}
          onCreate={onCreate}
          onEdit={onEdit}
          editingOpportunity={editingOpp}
          isLoading={formMutating}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Opportunity"
          description={`Are you sure you want to delete "${oppToDelete?.name}"?`}
          itemName={oppToDelete?.name}
          isLoading={deleteOpportunity.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
